import { Paper } from "@mui/material";
import { Box } from "@mui/system";
import { Button, CategoryTree } from "Atoms";
import {
  getFlatRequirementsBySourcePage,
  parseSourceRef,
} from "Utils/helperFunctions";
import { useBoundStore } from "Zustand/store/mainStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import TestRequirementForm from "./TesRequirementContentForm";
import { Add } from "@mui/icons-material";
// import PDFPreview from "Components/PDFPreview";
import SidePdfOverlay from "Components/SidePdfOverlay";

import { getAttachmentByNodeId } from "Zustand/services/attachmentService";

type Req = any; // use your Requirement type if you have one

type TestRequirementsProps = {
  entityType?: "testplan" | "testspecification";
  parentNodeId?: string;
};

const TestRequirements = ({
  entityType = "testplan",
  parentNodeId,
}: TestRequirementsProps) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [foundRequirement, setFoundRequirement] = useState<Req | null>(null);

    // PDF drawer state
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [pdfPage, setPdfPage] = useState<number>(1);

  const { selectedTestPlanContent } = useBoundStore();

  const openSourceDoc = useCallback(async (req:any) => {
    try {
      if (!req.sourcereference) return;
      const { attachmentNodeId, page } = parseSourceRef(req.sourcereference);
      if (!attachmentNodeId) return;

      const url = await getAttachmentByNodeId(attachmentNodeId);
      // keep drawer open; set new url/page → PDFPreview will jump
      setPdfUrl(url);
      setPdfPage(page);
      setPdfOpen(true);
    } catch (e) {
      console.error("Failed to open source doc", e);
    }
  }, []);


  // The viewer intentionally uses a single virtual group plus `flat` tree mode:
  // requirements are shown directly and sorted by source-reference page.
  const treedata: any[] = useMemo(() => {
    if (selectedTestPlanContent == null) return [];
    return [
      {
        nodeid: "test-requirements",
        name: "Test Requirements",
        requirements: getFlatRequirementsBySourcePage(selectedTestPlanContent),
      },
    ];
  }, [selectedTestPlanContent]);

  const handleSelect = (id: string) => setSelectedId(id);

  const handleNewTestRequirement = () => {
    setSelectedId(null);
    setFoundRequirement(null);
  };

  // find the req for the selected id
  useEffect(() => {
    if (!selectedId) {
      setFoundRequirement(null);
      return;
    }
    const req = treedata
      ?.flatMap((c: any) => c.requirements || [])
      ?.find((r: any) => r.nodeid === selectedId);
    setFoundRequirement(req || null);
  }, [selectedId, treedata]);

  // helper: pick first PDF url + page (adapt how you resolve page)
  // const pickPdfFromReq = (req: Req | null) => {
  //   if (!req?.hasattachment?.length) return { url: null, page: 1 };
  //   // your API structure sometimes is [{attachment:{file:{url...}}}]
  //   const entries = req.hasattachment;
  //   // handle either flat or wrapped
  //   const items = entries.map((a: any) => (a.attachment ? a.attachment : a));
  //   const firstPdf = items.find((it: any) =>
  //     /(\.pdf)(\?|$)/i.test(it?.file?.url || "")
  //   );
  //   return {
  //     url: firstPdf?.file?.url ?? null,
  //     page: (req as any)?.sourcePage ?? 1, // change if you keep page somewhere else
  //   };
  // };



  return (
    <Box display="flex" gap={2}>
      {/* Left: Tree */}
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          paddingBottom: "60px",
          padding: 3,
          minHeight: 400,
          flex: 1,
          p: 2,
        }}
      >
        <CategoryTree
          categories={treedata}
          onSelect={handleSelect}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          onSourceClick={(req:any) => openSourceDoc(req)} 
          flat
          searchable
          searchPlaceholder="Search ..."
        />
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            right: 16,
            display: "flex",
            gap: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleNewTestRequirement}
            startIcon={<Add />}
          >
            New
          </Button>
        </Box>
      </Paper>

      {/* Middle: Form */}
      <Paper
        elevation={3}
        sx={{
          position: "relative",
          paddingBottom: "60px",
          padding: 3,
          minHeight: 400,
          flex: pdfOpen ? 2 : 3,
          p: 3,
        }}
      >
        <TestRequirementForm
          requirement={foundRequirement}
          selectedId={selectedId}
          resetTree={handleNewTestRequirement}
          setSelectedId={setSelectedId}
          entityType={entityType}
          parentNodeId={parentNodeId}
        />
      </Paper>

      {/* Right: Inline PDF preview (third column) */}
      {/* {pdfOpen && pdfUrl && (
        <Paper
          elevation={3}
          sx={{
            position: "relative",
            paddingBottom: "60px",
            padding: 2,
            minHeight: 400,
            width: 520,
            flex: 1,
          }}
        >
          {/* <PDFPreview
            variant="inline"
            fileUrl={pdfUrl}
            pageNumber={3}
            height={600}
            showHeader
          /> */}

          {/* <PDFPreview
            variant="dialog"
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            fileUrl={pdfUrl}
            pageNumber={1}
          />
        </Paper>
      )} */}

      <SidePdfOverlay
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        pageNumber={pdfPage}
        title="Spec PDF"
      />
    </Box>
  );
};

export default TestRequirements;
